"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { UIMessage } from "ai";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { getLiveDelegationRequest, getLiveHistory, groupLiveTranscript, hasNewLiveRequest, splitLiveContent, type LiveTranscriptBubble, type LiveTranscript } from "@/lib/live-chat";
import { createLiveTiming, type LiveDelegateObserver } from "@/lib/live-chat-stream";
import { createLiveApprovalBridge, type LiveVoiceApproval, type LiveVoiceApprovalResponse } from "@/lib/live-chat-approval";

type LiveStatus = "idle" | "connecting" | "connected" | "closing";
type LiveConnection = {
  peer: RTCPeerConnection;
  channel: RTCDataChannel;
  audio: HTMLAudioElement;
  microphone?: MediaStream;
  abort: AbortController;
  ready: boolean;
  closing: boolean;
  timers: Set<ReturnType<typeof setTimeout>>;
  fragments: LiveTranscript[];
  seen: Set<string>;
  delegations: Set<string>;
  queue: Promise<void>;
  lastDelegatedOffsetMs: number;
  lastUserTranscriptAt: number;
  audioActive: boolean;
  pendingAudioTiming?: ReturnType<typeof createLiveTiming>;
  speechMonitor?: ReturnType<typeof setInterval>;
  approvals?: ReturnType<typeof createLiveApprovalBridge>;
  activeDelegationId?: string;
};

export function useLiveChat({ messages, onDelegate, scope, context, autoStart = false, onAutoStarted, pendingApproval = null, onApprovalResponse }: {
  messages: UIMessage[];
  onDelegate: (transcript: string, signal: AbortSignal, observer: LiveDelegateObserver) => Promise<void>;
  scope: string;
  context: string;
  autoStart?: boolean;
  onAutoStarted?: () => void;
  pendingApproval?: LiveVoiceApproval | null;
  onApprovalResponse?: (approval: LiveVoiceApproval, response: LiveVoiceApprovalResponse) => Promise<boolean>;
}) {
  const [status, setStatus] = useState<LiveStatus>("idle");
  const [transcript, setTranscript] = useState<{
    scope: string;
    bubbles: LiveTranscriptBubble[];
    anchorCount: number;
  }>({ scope, bubbles: [], anchorCount: 0 });
  const [working, setWorking] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [muted, setMuted] = useState(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [usageSeconds, setUsageSeconds] = useState(0);
  const connectionRef = useRef<LiveConnection | null>(null);
  const autoStarted = useRef(false);
  const latest = useRef({ messages, onDelegate, context, pendingApproval, onApprovalResponse });
  useEffect(() => {
    latest.current = { messages, onDelegate, context, pendingApproval, onApprovalResponse };
    connectionRef.current?.approvals?.sync();
  }, [messages, onDelegate, context, pendingApproval, onApprovalResponse]);

  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection?.ready || connection.closing || connection.channel.readyState !== "open") return;
    for (const content of splitLiveContent(context)) {
      connection.channel.send(JSON.stringify({ type: "session.thinking.append", delegation_id: null, content }));
    }
  }, [context]);

  const cleanup = useCallback((connection: LiveConnection) => {
    if (connectionRef.current !== connection) return;
    connectionRef.current = null;
    connection.approvals?.close();
    connection.abort.abort();
    connection.timers.forEach(clearTimeout);
    clearInterval(connection.speechMonitor);
    connection.microphone?.getTracks().forEach((track) => track.stop());
    connection.channel.close();
    connection.peer.close();
    connection.audio.pause();
    connection.audio.srcObject = null;
    setStatus("idle");
    setWorking(false);
    setSpeaking(false);
    setMuted(false);
    setPlaybackBlocked(false);
  }, []);

  const stop = useCallback(() => {
    const connection = connectionRef.current;
    if (!connection || connection.closing) return;
    connection.closing = true;
    connection.approvals?.close();
    // Stop capturing immediately; keep the transport alive for final usage.
    connection.microphone?.getTracks().forEach((track) => { track.enabled = false; });
    connection.abort.abort();
    if (connection.ready && connection.channel.readyState === "open") {
      setStatus("closing");
      connection.channel.send(JSON.stringify({ type: "session.close" }));
      connection.timers.add(setTimeout(() => {
        toast.warning("Live voice disconnected without final usage confirmation.");
        cleanup(connection);
      }, 15_000));
    } else cleanup(connection);
  }, [cleanup]);

  useEffect(() => () => {
    const connection = connectionRef.current;
    if (!connection) return;
    if (connection.ready && connection.channel.readyState === "open") {
      connection.channel.send(JSON.stringify({ type: "session.close" }));
    }
    cleanup(connection);
  }, [scope, cleanup]);

  const start = useCallback(async () => {
    if (connectionRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia || !window.RTCPeerConnection) {
      toast.error("Live voice needs a browser with microphone support on HTTPS or localhost.");
      return;
    }
    const peer = new RTCPeerConnection();
    const connection: LiveConnection = {
      peer, channel: peer.createDataChannel("oai-events"), audio: new Audio(),
      abort: new AbortController(), ready: false, closing: false, timers: new Set(),
      fragments: [], seen: new Set(), delegations: new Set(), queue: Promise.resolve(),
      lastDelegatedOffsetMs: -Infinity, lastUserTranscriptAt: -Infinity, audioActive: false,
    };
    connectionRef.current = connection;
    const startupTiming = createLiveTiming(crypto.randomUUID());
    startupTiming("connection_start");
    const isCurrent = () => connectionRef.current === connection && !connection.closing;
    const send = (event: object) => {
      if (isCurrent() && connection.ready && connection.channel.readyState === "open") {
        connection.channel.send(JSON.stringify(event));
      }
    };
    connection.approvals = createLiveApprovalBridge({
      getApproval: () => isCurrent() && connection.ready && latest.current.onApprovalResponse ? latest.current.pendingApproval : null,
      canListen: () => isCurrent() && connection.ready && !connection.audio.paused &&
        Boolean(connection.microphone?.getAudioTracks().some((track) => track.enabled)),
      send: (type, text) => {
        for (const content of splitLiveContent(text)) {
          send({ type, event_id: crypto.randomUUID(), delegation_id: connection.activeDelegationId ?? null, content });
        }
      },
      respond: (approval, response) => latest.current.onApprovalResponse?.(approval, response) ?? Promise.resolve(false),
    });
    setStatus("connecting");
    setTranscript({ scope, bubbles: [], anchorCount: latest.current.messages.length });
    setUsageSeconds(0);
    connection.audio.autoplay = true;
    connection.audio.setAttribute("playsinline", "");
    const startupTimer = setTimeout(() => {
      if (isCurrent() && !connection.ready) {
        toast.error("Live voice connection timed out.");
        cleanup(connection);
      }
    }, 40_000);
    connection.timers.add(startupTimer);

    peer.addEventListener("track", ({ track }) => {
      if (!isCurrent()) return;
      connection.audio.srcObject = new MediaStream([track]);
      void connection.audio.play().catch(() => { if (isCurrent()) setPlaybackBlocked(true); });
      // Remote media energy tracks speech, unlike `playing`, which stays true
      // for a continuous WebRTC stream even during silence. No second audio
      // output or microphone analyser is needed.
      clearInterval(connection.speechMonitor);
      let checking = false;
      let lastEnergy = 0;
      let lastDuration = 0;
      let lastSpeechAt = -Infinity;
      connection.speechMonitor = setInterval(() => {
        if (!isCurrent() || checking) return;
        checking = true;
        void peer.getStats().then((stats) => {
          if (!isCurrent()) return;
          let active = false;
          stats.forEach((report) => {
            if (report.type !== "inbound-rtp" || report.kind !== "audio") return;
            const audio = report as RTCInboundRtpStreamStats;
            if (typeof audio.audioLevel === "number") active ||= audio.audioLevel > 0.01;
            if (typeof audio.totalAudioEnergy === "number" && typeof audio.totalSamplesDuration === "number") {
              const duration = audio.totalSamplesDuration - lastDuration;
              if (lastDuration > 0 && duration > 0) {
                active ||= (audio.totalAudioEnergy - lastEnergy) / duration > 0.0001;
              }
              lastEnergy = audio.totalAudioEnergy;
              lastDuration = audio.totalSamplesDuration;
            }
          });
          if (active) lastSpeechAt = performance.now();
          if (active && !connection.audioActive && !connection.audio.paused) {
            startupTiming("first_audio");
            connection.pendingAudioTiming?.("audio_onset_after_result");
            connection.pendingAudioTiming = undefined;
          }
          connection.audioActive = active;
          setSpeaking(!connection.audio.paused && performance.now() - lastSpeechAt < 350);
        }).catch(() => {
          if (isCurrent()) setSpeaking(false);
        }).finally(() => { checking = false; });
      }, 150);
    });
    peer.addEventListener("connectionstatechange", () => {
      if (connectionRef.current === connection && peer.connectionState === "failed") {
        toast.error("Live voice lost its connection.");
        cleanup(connection);
      }
    });
    connection.channel.addEventListener("close", () => {
      if (connectionRef.current === connection) {
        toast.warning("Live voice disconnected without final usage confirmation.");
        cleanup(connection);
      }
    });
    connection.channel.addEventListener("message", ({ data }) => {
      if (connectionRef.current !== connection) return;
      let event;
      try { event = JSON.parse(data); } catch { return; }
      if (!event || typeof event.type !== "string") return;
      if (typeof event.event_id === "string") {
        if (connection.seen.has(event.event_id)) return;
        connection.seen.add(event.event_id);
      }
      if (event.type === "session.closed") {
        if (Number.isFinite(event.usage?.seconds)) setUsageSeconds(event.usage.seconds);
        cleanup(connection);
        return;
      }
      // Accept final caption fragments while graceful shutdown drains audio.
      if (connection.closing && event.type !== "session.input_transcript.delta" &&
        event.type !== "session.output_transcript.delta") return;
      if (event.type === "session.started") {
        clearTimeout(startupTimer);
        connection.ready = true;
        startupTiming("connected");
        setStatus("connected");
        connection.approvals?.sync();
        for (const content of splitLiveContent(latest.current.context)) {
          send({ type: "session.thinking.append", delegation_id: null, content });
        }
        // Keep accidentally abandoned sessions bounded.
        connection.timers.add(setTimeout(stop, 20 * 60_000));
      } else if (event.type === "session.usage.updated") {
        if (Number.isFinite(event.usage?.seconds)) setUsageSeconds(event.usage.seconds);
      } else if (event.type === "error") {
        toast.error("Live voice encountered an error. Please restart the conversation.");
        stop();
      } else if (event.type === "session.input_transcript.delta" || event.type === "session.output_transcript.delta") {
        if (typeof event.delta !== "string" || !Number.isFinite(event.start_ms) || !Number.isFinite(event.end_ms)) return;
        const role = event.type === "session.input_transcript.delta" ? "user" : "assistant";
        if (role === "user") connection.lastUserTranscriptAt = performance.now();
        const fragment: LiveTranscript = { role, text: event.delta, startMs: event.start_ms, endMs: event.end_ms,
          anchorCount: latest.current.messages.length };
        connection.fragments.push(fragment);
        connection.approvals?.observe(fragment);
        setTranscript((previous) => ({ ...previous, bubbles: groupLiveTranscript(connection.fragments) }));
      } else if (event.type === "session.delegation.created" && event.delegation?.target === "client") {
        const id = event.delegation.id;
        const offsetMs = event.offset_ms;
        if (typeof id !== "string" || !Number.isFinite(offsetMs) || connection.delegations.has(id)) return;
        connection.delegations.add(id);
        // A confirmation reply resolves the current SDK approval directly.
        // Queueing it behind that same approval would deadlock the conversation.
        if (connection.approvals?.hasPending()) {
          send({ type: "session.thinking.append", delegation_id: id,
            content: "An action is awaiting confirmation. The application handles the user's yes/no reply directly. Do not start another backend task or claim that the action succeeded." });
          return;
        }
        const timing = createLiveTiming(id);
        timing("delegation_received");
        // Serialize backend turns: a second delegation must not overwrite the
        // first turn or bypass an approval that is still waiting in the chat.
        connection.queue = connection.queue.then(async () => {
          if (!isCurrent()) return;
          const transcript = getLiveDelegationRequest(connection.fragments, offsetMs, connection.lastDelegatedOffsetMs);
          if (!transcript) {
            send({ type: "session.thinking.append", delegation_id: id,
              content: "No new request transcript is available for this delegation. Do not repeat a completed action. Clarify the request if needed." });
            return;
          }
          setWorking(true);
          connection.activeDelegationId = id;
          let buffered: string[] = [];
          let flushTimer: ReturnType<typeof setTimeout> | undefined;
          let forwarded = false;
          const flush = () => {
            if (flushTimer) { clearTimeout(flushTimer); connection.timers.delete(flushTimer); flushTimer = undefined; }
            if (!isCurrent() || !buffered.length) return;
            // Let split transcript deltas settle before classifying "okay ..."
            // as an acknowledgment or the start of a changed request.
            const hasLaterSpeech = connection.fragments.some((part) => part.role === "user" && part.startMs > offsetMs);
            const settleMs = hasLaterSpeech ? 500 - (performance.now() - connection.lastUserTranscriptAt) : 0;
            if (settleMs > 0) {
              flushTimer = setTimeout(flush, settleMs);
              connection.timers.add(flushTimer);
              return;
            }
            const superseded = hasNewLiveRequest(connection.fragments, offsetMs);
            const text = buffered.join("\n");
            buffered = [];
            for (const content of splitLiveContent(text)) {
              send({ type: superseded ? "session.thinking.append" : "session.commentary.append",
                event_id: crypto.randomUUID(), delegation_id: id, content });
            }
            timing(superseded ? "result_superseded" : "first_result_forwarded");
            if (!superseded && !forwarded) { connection.pendingAudioTiming = timing; forwarded = true; }
          };
          try {
            timing("backend_start");
            await latest.current.onDelegate(transcript, connection.abort.signal, {
              onFirstText: () => timing("first_backend_text"),
              onText: (text) => { if (isCurrent()) { buffered.push(text); flush(); } },
            });
            if (!isCurrent()) return;
            connection.lastDelegatedOffsetMs = Math.max(connection.lastDelegatedOffsetMs, offsetMs);
            timing("backend_complete");
            flush();
          } catch {
            buffered = [];
            if (flushTimer) { clearTimeout(flushTimer); connection.timers.delete(flushTimer); }
            timing("backend_failed");
            send({ type: "session.commentary.append", delegation_id: id,
              content: "The backend request did not complete. Do not claim success. Ask the user to check the chat and try again." });
          } finally {
            if (connection.activeDelegationId === id) connection.activeDelegationId = undefined;
            if (isCurrent()) setWorking(false);
          }
        }).catch(() => { if (isCurrent()) setWorking(false); });
      }
    });

    try {
      const microphone = await navigator.mediaDevices.getUserMedia({ audio: true });
      startupTiming("microphone_ready");
      if (!isCurrent()) { microphone.getTracks().forEach((track) => track.stop()); return; }
      connection.microphone = microphone;
      for (const track of microphone.getAudioTracks()) peer.addTrack(track, microphone);
      const [offer, { data: { session } }] = await Promise.all([peer.createOffer(), supabase.auth.getSession()]);
      if (!isCurrent()) return;
      await peer.setLocalDescription(offer);
      startupTiming("ice_gathering_start");
      if (peer.iceGatheringState !== "complete") {
        await new Promise<void>((resolve, reject) => {
          const finish = (error?: Error) => {
            clearTimeout(timer);
            peer.removeEventListener("icegatheringstatechange", onState);
            connection.abort.signal.removeEventListener("abort", onAbort);
            if (error) reject(error); else resolve();
          };
          const onState = () => { if (peer.iceGatheringState === "complete") finish(); };
          const onAbort = () => finish(new Error("Cancelled"));
          const timer = setTimeout(() => finish(new Error("Microphone connection timed out.")), 10_000);
          peer.addEventListener("icegatheringstatechange", onState);
          connection.abort.signal.addEventListener("abort", onAbort, { once: true });
          if (connection.abort.signal.aborted) onAbort(); else onState();
        });
      }
      if (!isCurrent()) return;
      startupTiming("ice_gathering_complete");
      if (!session) throw new Error("Please sign in again.");
      startupTiming("session_request_start");
      const response = await fetch("/api/chat/live", {
        method: "POST", signal: connection.abort.signal,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ sdp: peer.localDescription?.sdp, history: getLiveHistory(latest.current.messages) }),
      });
      const result = await response.json();
      if (!isCurrent()) return;
      if (!response.ok) throw new Error(result.error || "Could not start live voice.");
      startupTiming("session_request_complete");
      await peer.setRemoteDescription({ type: "answer", sdp: result.transport.sdp });
    } catch (error) {
      if (isCurrent()) {
        toast.error(error instanceof Error ? error.message : "Could not start live voice.");
        cleanup(connection);
      }
    }
  }, [cleanup, stop, scope]);

  useEffect(() => {
    if (!autoStart || autoStarted.current) return;
    // React Strict Mode replays mount effects and cleans up connections in
    // between. Defer until that replay settles, so a one-shot project launch
    // isn't consumed by a connection that is immediately torn down.
    const timer = setTimeout(() => {
      autoStarted.current = true;
      void start();
      onAutoStarted?.();
    }, 0);
    return () => clearTimeout(timer);
  }, [autoStart, onAutoStarted, start]);

  const toggleMuted = useCallback(() => {
    const connection = connectionRef.current;
    if (!connection?.ready || connection.closing) return;
    setMuted((previous) => {
      connection.microphone?.getAudioTracks().forEach((track) => { track.enabled = previous; });
      return !previous;
    });
  }, []);

  const playAudio = useCallback(async () => {
    const connection = connectionRef.current;
    if (!connection) return;
    try {
      await connection.audio.play();
      if (connectionRef.current === connection) setPlaybackBlocked(false);
    } catch { toast.error("Your browser could not play live audio."); }
  }, []);

  return { status, transcript: transcript.scope === scope ? transcript.bubbles : [],
    transcriptAnchor: transcript.anchorCount,
    working, speaking, muted, playbackBlocked, usageSeconds, start, stop, toggleMuted, playAudio };
}
